import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import ReactDOM from "react-dom";
import "../modal.css";
import { toast } from "react-toastify";
import { usePackerDispatcher } from "../../../store/usePackerDispatcher";
import { Eye, EyeOff } from 'lucide-react';

const AddPackerDispatcherPopup = ({ onClose, fetchData, filter, editData = {}, setCurrentPage }) => {
  const { createPackerDispatcher,editPackerDispatcher } = usePackerDispatcher();
  const [showPassword, setShowPassword] = useState(false);

  const isEditing = Object.keys(editData).length > 0;

  const handleAddMember = async (values) => {
    try {
      const response = await createPackerDispatcher(values);
      console.log("API Response:", response);

      if (response.status) {
        const message = response.data?.data?.message || "Member added successfully!";
        toast.success(message, {toastId: 'successMessageAddMember'});
        onClose();
        fetchData(filter);
        setCurrentPage(1)
      } else {
        const errorMessage = response.data?.data?.message || "Failed to add member.";
        toast.error(errorMessage, {toastId: 'errorMessageAddPackerDispatcher'});
      }
    } catch (e) {
      console.error("Error in handleAddMember:", e);
    }
  };

  const handleEditMember = async (values) => {
    const updatedValues = {...values, user_id: editData?.user_id}

    try {
      const response = await editPackerDispatcher(updatedValues);
      console.log("API Response:", response);

      if (response.status) {
        const message = response.data?.data?.message || "Member updated successfully!";
        toast.success(message, {toastId: 'successMessageEditPacker'});
        onClose();
        fetchData(filter);
        setCurrentPage(1)
      } else {
        const errorMessage = response.data?.data?.message || "Failed to update member.";
        toast.error(errorMessage, {toastId: 'errorMessageEditPackerDispatcher'});
      }
    } catch (e) {
      console.error("Error in handleAddMember:", e);
    }
  };

  const formik = useFormik({
    initialValues: {
      role: editData?.role ?? "",
      mobile: editData?.mobile ?? "",
      firstName: editData?.firstName ?? "",
      lastName: editData?.lastName ?? "",
      password: "",
      block_user: editData?.block_user === "0" ? "0":"1",
    },
    validationSchema: Yup.object({
      role: Yup.number().required("Role is required"),
      mobile: Yup.string()
        .matches(/^\d{10}$/, "Enter a valid 10-digit mobile number")
        .required("Mobile number is required"),
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      password: isEditing 
  ? Yup.string().notRequired() 
  : Yup.string()
      .matches(/^\d{4}$/, "Password must be exactly 4 digits")
      .required("Password is required"),
      block_user: isEditing ? Yup.string().required("Active status is required") : Yup.string().notRequired(),
    }),
    onSubmit: (values) => {
      isEditing ? handleEditMember(values) : handleAddMember(values);
    },
  });

  return ReactDOM.createPortal(
    <>
      <div className="custom-modal-backdrop" 
      // onClick={onClose}
      ></div>
      <div className="custom-modal">
        <div className="custom-modal-content">
          <div className="custom-modal-header">
            <h5 className="custom-modal-title">{isEditing ? "Edit Packer/Dispatcher" : "Add Packer/Dispatcher"}</h5>
            <button className="custom-close-btn" onClick={onClose}>&times;</button>
          </div>
          <div className="custom-modal-body">
            <form onSubmit={formik.handleSubmit}>
            <div className="relative mb-4">
            <label className="mt-2">Enter First Name</label>
            <input
              type="text"
              className="custom-input mt-2"
              placeholder="Enter First Name"
              {...formik.getFieldProps("firstName")}
              onChange={(e) => {
                e.target.value = e.target.value.replace(/[^a-zA-Z ]/g, "");
                formik.handleChange(e)}}
            />
            {formik.touched.firstName && formik.errors.firstName && (
              <div className="absolute -bottom-5 text-red-500 text-sm mt-1">
                {formik.errors.firstName}
              </div>
            )}
          </div>

          <div className="relative mb-4">
            <label className="mt-2">Enter Last Name</label>
            <input
              type="text"
              className="custom-input mt-2"
              placeholder="Enter Last Name"
              {...formik.getFieldProps("lastName")}
              onChange={(e) => {
                e.target.value = e.target.value.replace(/[^a-zA-Z ]/g, "");
                formik.handleChange(e)}}
            />
            {formik.touched.lastName && formik.errors.lastName && (
              <div className="absolute -bottom-5 text-red-500 text-sm mt-1">
                {formik.errors.lastName}
              </div>
            )}
          </div>

          <div className="relative mb-4">
            <label className="mt-2">Enter Mobile Number</label>
            <input
              type="text"
              maxLength={10}
              className="custom-input mt-2"
              placeholder="Enter Mobile Number"
              {...formik.getFieldProps("mobile")}
            />
            {formik.touched.mobile && formik.errors.mobile && (
              <div className="absolute -bottom-5 text-red-500 text-sm mt-1">
                {formik.errors.mobile}
              </div>
            )}
          </div>
              {!isEditing && (
                <div className="relative mb-4">
                  <label className="mt-2">Enter Password</label>
                  <input 
                  type={showPassword ? "text" : "password"}
                  className="custom-input mt-2" placeholder="Password" {...formik.getFieldProps("password")}
                  onChange={(e) => {
                    e.target.value = e.target.value.replace(/\D/g, "").slice(0, 4);
                    formik.handleChange(e);
                  }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-14 transform -translate-y-1/2 text-gray-500 focus:outline-none"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                  {formik.touched.password && formik.errors.password && (
                    <div className="absolute -bottom-5 text-red-500 text-sm mt-1">{formik.errors.password}</div>
                  )}
                </div>
              )}
              <div className="relative mb-4">
                <label className="mt-2">Select Role</label>
                <select className="custom-input mt-2" {...formik.getFieldProps("role")}>
                  <option value="">Select Role</option>
                  <option value="1">Packer</option>
                  <option value="2">Dispatcher</option>
                </select>
                {formik.touched.role && formik.errors.role && (
                  <div className="absolute -bottom-5 text-red-500 text-sm mt-1">{formik.errors.role}</div>
                )}
              </div>
              {isEditing && (
                <div className="relative mb-4">
                  <label className="mt-2">Status</label>
                  <select className="custom-input mt-2" {...formik.getFieldProps("block_user")}>
                    <option value="0">Active</option>
                    <option value="1">Blocked</option>
                  </select>
                  {formik.touched.block_user && formik.errors.block_user && (
                    <div className="absolute -bottom-5 text-red-500 text-sm mt-1">{formik.errors.block_user}</div>
                  )}
                </div>
              )}
              <div className="custom-modal-footer">
                <button type="button" className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 shadow-xs ring-gray-300 ring-inset hover:bg-gray-50 sm:mt-0 sm:w-auto" onClick={onClose}>Close</button>
                <button type="submit" className="inline-flex w-full justify-center rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-emerald-500 sm:ml-2 sm:w-auto">{isEditing ? "Update" : "Submit"}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>,
    document.getElementById("popup-root")
  );
};

export default AddPackerDispatcherPopup;
